import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0, useBouncer } from 'bouncer';
import { uniqBy } from 'lodash/array';
import { filter, includes, sortBy } from 'lodash/collection';
import { isArray, isEmpty, isString } from 'lodash/lang';

import { StudyApi } from '../../../../../api';
import Select from '../../../../../common/data-entry/Select';
import ModalBoxes from '../../../../../common/feedback/ModalBoxes/ModalBoxes';
import Button from '../../../../../common/general/Button';
import ButtonGroup from '../../../../../common/general/ButtonGroup';
import useAppInfo from '../../../../../common/hooks/useAppInfo';
import { onRequestError } from '../../../../../services/handlers';
import { NSPS_STUDY } from '../../../../../services/i18n/namespaces';

import EncounterPrintMultiselect from './EncounterPrintMultiselect';

import './PrintEncountersGeneratorModal.scss';

const situationalEpochId = 'situational-epoch-id';
const situationalEpochName = 'Situational';
const allowedProtocolStatusesToDisplay = ['Active', 'Draft', 'Retired'];

export default function PrintPatientEncountersGeneratorModal({ modalBox, studyIdentifier }) {
  const { t } = useTranslation();
  const [idTokenKey, setIdTokenKey] = useState(null);

  const [protocols, setProtocols] = useState([]);
  const [selectedProtocol, setSelectedProtocol] = useState(null);

  const [allEncounters, setAllEncounters] = useState([]);
  const [selectedEncounters, setSelectedEncounters] = useState([]);

  const [displayedItemGroups, setDisplayedItemGroups] = useState([]);
  const [selectedItemGroups, setSelectedItemGroups] = useState([]);

  const selectedProtocolVersion = selectedProtocol?.version;

  const { jwtLocalStorageKey } = useBouncer();
  const { user } = useAuth0();
  const appInfo = useAppInfo();

  const isAllowedToGenerate = useMemo(
    function() {
      return isString(idTokenKey) && isArray(selectedItemGroups) && !isEmpty(selectedItemGroups);
    },
    [idTokenKey, selectedItemGroups]
  );

  const linkToPrintPage = useMemo(
    function() {
      if (!selectedProtocolVersion) {
        return '#';
      }
      return `/print-version-of-encounters.html?p=${selectedProtocolVersion}&i=${studyIdentifier}&k=${idTokenKey}`;
    },
    [idTokenKey, studyIdentifier, selectedProtocolVersion]
  );

  useEffect(
    function() {
      setIdTokenKey(jwtLocalStorageKey);
    },
    [jwtLocalStorageKey, appInfo?.auth0ClientId, user?.sub]
  );

  useEffect(() => {
    StudyApi.getProtocolsByStudyIdentifierWithIncompleteCheck(studyIdentifier).then(res => {
      setProtocols(
        filter(res.data, protocol => includes(allowedProtocolStatusesToDisplay, protocol.status)).map(mapProtocol)
      );
    }, onRequestError);
  }, [studyIdentifier]);

  useEffect(
    function() {
      if (!isEmpty(protocols) && !selectedProtocol) {
        const activeProtocol = protocols.find(protocol => protocol.status === 'Active');
        onProtocolChange(activeProtocol || protocols[0]);
      }
    }, // eslint-disable-next-line
    [protocols]
  );

  useEffect(
    function() {
      let encounters = [];
      if (!isEmpty(selectedProtocol?.epochs)) {
        encounters = selectedProtocol.epochs.flatMap(epoch =>
          epoch.encounters.map(encounter => ({
            ...encounter,
            epochId: epoch.id,
            epochName: epoch.name
          }))
        );
      }
      let situationalEncounters = [];
      if (!isEmpty(selectedProtocol?.situationalEncounter)) {
        situationalEncounters = alignSituationalEncountersWithEncounters(selectedProtocol.situationalEncounter);
      }
      const combinedEncounters = [...encounters, ...situationalEncounters];
      setAllEncounters(combinedEncounters);
      setSelectedEncounters(combinedEncounters);
    },
    [selectedProtocol]
  );

  useEffect(
    function() {
      setDisplayedItemGroups(
        sortBy(getUniqueItemGroupsFromEncounters(selectedEncounters), elem => elem.name.toUpperCase())
      );
      setSelectedItemGroups(
        sortBy(getUniqueItemGroupsFromEncounters(selectedEncounters), elem => elem.name.toUpperCase())
      );
    },
    [selectedEncounters]
  );
  return (
    <>
      <ModalBoxes.Header>{t('print.title', { ns: NSPS_STUDY })}</ModalBoxes.Header>
      <ModalBoxes.Body>
        <div className="encounters-selector">
          <Select
            className="protocol-select"
            dataSource={protocols}
            label={t('print.protocolVersion', { ns: NSPS_STUDY })}
            required
            value={selectedProtocol}
            onChange={onProtocolChange}
            clearable={false}
          />
          <h3>{t('print.encounters', { ns: NSPS_STUDY })}</h3>
          {!isEmpty(allEncounters) && (
            <EncounterPrintMultiselect
              selectedOptions={selectedEncounters}
              options={allEncounters}
              onChange={onEncountersSelect}
              groupByKey="epochId"
              groupLabelField="epochName"
              defaultSelectAll
            />
          )}
        </div>
        <div className="item-groups-selector">
          <h3>{t('print.procedures', { ns: NSPS_STUDY })}</h3>
          {displayedItemGroups && displayedItemGroups.length > 0 ? (
            <EncounterPrintMultiselect
              selectedOptions={selectedItemGroups}
              options={displayedItemGroups}
              onChange={onItemGroupsSelect}
              id="uuid"
              defaultSelectAll
            />
          ) : (
            <div className="allert-unselected-encounters">
              <div className="alert alert-light" role="alert">
                {t('print.selectAtLeastOneEncounter', { ns: NSPS_STUDY })}
              </div>
            </div>
          )}
        </div>
      </ModalBoxes.Body>
      <ModalBoxes.Footer>
        <ButtonGroup>
          <Button priority="medium" onClick={modalBox.close}>
            {t('close')}
          </Button>
          <Button target="_blank" href={linkToPrintPage} onClick={generate} disabled={!isAllowedToGenerate}>
            {t('print.generate', { ns: NSPS_STUDY })}
          </Button>
        </ButtonGroup>
      </ModalBoxes.Footer>
    </>
  );

  function onEncountersSelect(newSelectedEncounters) {
    setSelectedEncounters(newSelectedEncounters);
  }

  function onItemGroupsSelect(newSelectedItemGroups) {
    setSelectedItemGroups(newSelectedItemGroups);
  }

  function onProtocolChange(protocol) {
    if (protocol?.id !== selectedProtocol?.id) {
      StudyApi.getProtocolsByStudyIdentifierWithIncompleteCheck(studyIdentifier).then(res => {
        setProtocols(
          filter(res.data, prt => includes(allowedProtocolStatusesToDisplay, prt.status))
            .map(mapProtocol)
            .filter(prt => prt.id !== protocol.id)
        );
        protocol = protocols.find(prt => prt.id === protocol.id);
      }, onRequestError);
      setSelectedProtocol(enrichItemGroups(protocol));
    }
  }

  function generate() {
    saveItemGroupsToLocalStorage(selectedEncounters, selectedItemGroups);
  }
}

function getUniqueItemGroupsFromEncounters(encounters) {
  const allItemGroups = encounters.flatMap(({ itemGroups }) => itemGroups);
  return uniqBy(
    allItemGroups.map(({ uuid, name }) => ({ uuid, name })),
    'uuid'
  );
}

function saveItemGroupsToLocalStorage(selectedEncounters, selectedItemGroups) {
  const itemGroupUUIDs = selectedItemGroups.map(({ uuid }) => uuid);
  const itemGroupsFromAllSelectedEncounters = selectedEncounters.flatMap(({ itemGroups }) =>
    itemGroups.filter(({ uuid }) => itemGroupUUIDs.includes(uuid))
  );

  localStorage.setItem(
    'encountersByIGId',
    JSON.stringify(getEncountersByItemGroup(itemGroupsFromAllSelectedEncounters))
  );
  localStorage.setItem('epvItemGroups', JSON.stringify(itemGroupUUIDs));
}

function mapProtocol(protocol) {
  return {
    id: protocol.protocolId,
    name: `${protocol.name} v${protocol.versionId}.0 (${protocol.status})`,
    epochs: protocol.epochs,
    situationalEncounter: protocol.situationalEncounter,
    version: protocol.versionId,
    status: protocol.status
  };
}

function enrichItemGroups(protocol) {
  /* TODO: do on protocols set and do immutable*/
  protocol.epochs = protocol.epochs.map(epoch => {
    epoch.encounters = epoch.encounters.map(encounter => {
      encounter.itemGroups = encounter.itemGroups.map(itemGroup => {
        return {
          encounterName: encounter.name,
          epochName: epoch.name,
          ...itemGroup
        };
      });
      return encounter;
    });
    return epoch;
  });
  return protocol;
}

function getEncountersByItemGroup(allItemGroups) {
  const map = {};
  allItemGroups.forEach(curr => {
    const key = curr.uuid;
    const value = curr.encounterName;
    if (key in map) {
      map[key].push(value);
    } else {
      map[key] = [value];
    }
  });
  return map;
}

function alignSituationalEncountersWithEncounters(situationalEncounters) {
  return situationalEncounters.map(encounter => ({
    epochId: situationalEpochId,
    epochName: situationalEpochName,
    id: encounter.id,
    name: encounter.name,
    itemGroups: encounter.elements.map(element => ({
      epochId: situationalEpochId,
      epochName: situationalEpochName,
      encounterName: encounter.name,
      uuid: element.itemGroupTemplateVersionId,
      name: element.elementName
    }))
  }));
}

PrintPatientEncountersGeneratorModal.className = 'print-version-generator-of-encounters';
PrintPatientEncountersGeneratorModal.size = 'w950';
